import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle2, AlertCircle, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';

const POLL_INTERVAL = 2000;
const MAX_ATTEMPTS = 12;

const BillingSuccess = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user } = useAuth();
  const sessionId = params.get('session_id');
  const [state, setState] = useState({ loading: true, paid: false, planId: null, error: null });

  useEffect(() => {
    let attempts = 0;
    let cancelled = false;
    if (!sessionId) {
      setState({ loading: false, paid: false, error: 'Missing session id' });
      return;
    }
    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/billing/checkout-status/${sessionId}`);
        if (data.paymentStatus === 'paid') {
          setState({ loading: false, paid: true, planId: data.planId });
          return;
        }
        if (data.status === 'expired') {
          setState({ loading: false, paid: false, error: 'Checkout session expired' });
          return;
        }
        attempts += 1;
        if (attempts < MAX_ATTEMPTS) {
          setTimeout(poll, POLL_INTERVAL);
        } else {
          setState({ loading: false, paid: false, error: 'Timed out checking payment. Please refresh in a moment.' });
        }
      } catch (err) {
        setState({ loading: false, paid: false, error: err?.response?.data?.detail || 'Could not verify payment' });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#fdfaf6] flex items-center justify-center px-5">
      <div className="max-w-md w-full bg-white rounded-3xl border border-slate-200 shadow-xl p-10 text-center">
        {state.loading && (
          <>
            <Loader2 className="w-12 h-12 mx-auto text-orange-500 animate-spin" />
            <h1 className="mt-5 text-2xl font-extrabold text-slate-900">Confirming your payment…</h1>
            <p className="mt-1 text-slate-500">This usually takes a few seconds.</p>
          </>
        )}
        {!state.loading && state.paid && (
          <>
            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 className="w-9 h-9 text-emerald-600" />
            </div>
            <h1 className="mt-5 text-2xl font-extrabold text-slate-900">You're on the {state.planId || 'paid'} plan! 🎉</h1>
            <p className="mt-1 text-slate-600">Welcome aboard{user?.name ? `, ${user.name.split(' ')[0]}` : ''}. Time to grow your business.</p>
            <Button onClick={() => navigate('/onboarding')} className="mt-6 bg-slate-900 hover:bg-slate-800 text-white rounded-xl h-11 px-6">
              <Sparkles className="w-4 h-4 mr-2" /> Continue to setup
            </Button>
          </>
        )}
        {!state.loading && !state.paid && (
          <>
            <div className="w-16 h-16 mx-auto rounded-full bg-amber-100 flex items-center justify-center">
              <AlertCircle className="w-9 h-9 text-amber-600" />
            </div>
            <h1 className="mt-5 text-2xl font-extrabold text-slate-900">Payment not confirmed</h1>
            <p className="mt-1 text-slate-600 text-sm">{state.error || 'No problem — try again from the pricing page.'}</p>
            <Button onClick={() => navigate('/#pricing')} className="mt-6 bg-slate-900 hover:bg-slate-800 text-white rounded-xl h-11 px-6">
              Back to pricing
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default BillingSuccess;
